/**
 * 3D Room Visualizer — Wall Mesh Builder
 *
 * Converts board layout data into Three.js meshes using merged BufferGeometry
 * for performance. Creates board-covered walls with visible seams and cut lines.
 */

import * as THREE from 'three';
import { BoardRect, WallBoardLayout, WallFace, CeilingBoardLayout } from './types';
import { createBoardMaterial, createWallShellMaterial } from './materialFactory';

// Inset from wall plane to prevent z-fighting (feet)
const WALL_INSET = 0.01;

// ============================================================================
// WALL MESH BUILDING
// ============================================================================

/**
 * Build a complete wall group with board meshes for a single wall face.
 * Returns a THREE.Group containing:
 *   - Dark backing plane (for seam visibility)
 *   - Merged board meshes (one per color)
 *   - Cut line overlays
 */
export function buildWallMesh(
  layout: WallBoardLayout,
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.Group {
  const group = new THREE.Group();
  group.userData = { wallFace: face };

  // Collect all boards from all sections
  const allBoards: BoardRect[] = [];
  for (const section of layout.sections) {
    allBoards.push(...section.boards);
  }

  if (allBoards.length === 0) return group;

  // Group boards by color
  const boardsByColor = new Map<string, BoardRect[]>();
  for (const board of allBoards) {
    const key = board.color;
    if (!boardsByColor.has(key)) {
      boardsByColor.set(key, []);
    }
    boardsByColor.get(key)!.push(board);
  }

  // Create merged mesh per color
  for (const [colorHex, boards] of boardsByColor) {
    const isWhite = colorHex.toUpperCase() === '#FFFFFF';
    const materialColor = isWhite ? 'White' : 'Gray';
    const material = createBoardMaterial(materialColor);
    const mesh = createMergedBoardMesh(boards, face, roomLength, roomWidth, roomHeight, material);
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  // Add seam lines between all boards (visible grid)
  const seamLines = createSeamLineOverlay(allBoards, face, roomLength, roomWidth, roomHeight);
  if (seamLines) {
    group.add(seamLines);
  }

  // Add SlatWall groove details
  const slatWallBoards = allBoards.filter(b => b.productType === 'SlatWall');
  if (slatWallBoards.length > 0) {
    const grooves = createSlatWallGrooves(slatWallBoards, face, roomLength, roomWidth, roomHeight);
    if (grooves) group.add(grooves);
  }

  return group;
}

/**
 * Build a ceiling group with board meshes.
 */
export function buildCeilingMesh(
  layout: CeilingBoardLayout,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.Group {
  const group = new THREE.Group();
  group.userData = { wallFace: 'ceiling' };

  if (layout.boards.length === 0) return group;

  // Group boards by color
  const boardsByColor = new Map<string, BoardRect[]>();
  for (const board of layout.boards) {
    if (!boardsByColor.has(board.color)) {
      boardsByColor.set(board.color, []);
    }
    boardsByColor.get(board.color)!.push(board);
  }

  // Create merged mesh per color
  for (const [colorHex, boards] of boardsByColor) {
    const isWhite = colorHex.toUpperCase() === '#FFFFFF';
    const material = createBoardMaterial(isWhite ? 'White' : 'Gray');

    const mesh = createMergedCeilingBoardMesh(boards, roomLength, roomWidth, roomHeight, material);
    group.add(mesh);
  }

  // Add seam lines to ceiling boards
  const seamLines = createCeilingSeamLines(layout.boards, roomLength, roomHeight);
  if (seamLines) group.add(seamLines);

  return group;
}

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Create a dark backing plane for a wall face.
 */
function createShellPlane(
  face: WallFace,
  wallLength: number,
  wallHeight: number,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(wallLength, wallHeight);
  const mat = createWallShellMaterial();
  const mesh = new THREE.Mesh(geo, mat);

  switch (face) {
    case 'front':
      mesh.position.set(roomLength / 2, roomHeight / 2, 0);
      // Normal faces +Z (into room)
      break;
    case 'back':
      mesh.position.set(roomLength / 2, roomHeight / 2, roomWidth);
      mesh.rotation.y = Math.PI;
      break;
    case 'left':
      mesh.position.set(0, roomHeight / 2, roomWidth / 2);
      mesh.rotation.y = Math.PI / 2;
      break;
    case 'right':
      mesh.position.set(roomLength, roomHeight / 2, roomWidth / 2);
      mesh.rotation.y = -Math.PI / 2;
      break;
  }

  return mesh;
}

/**
 * Transform a 2D board position to 3D world coordinates for a wall face.
 * Returns the 4 corner positions of the board quad.
 *
 * Board 2D coordinate system: x = left-to-right on wall, y = bottom-to-top
 */
function boardTo3D(
  board: BoardRect,
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  _roomHeight: number
): [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3] {
  const { x, y, width, height } = board;

  // 4 corners in 2D wall space: BL, BR, TR, TL
  const bl2d = { x, y };
  const br2d = { x: x + width, y };
  const tr2d = { x: x + width, y: y + height };
  const tl2d = { x, y: y + height };

  const corners2d = [bl2d, br2d, tr2d, tl2d];

  const corners3d = corners2d.map(c => {
    switch (face) {
      case 'front':
        // z=0 plane, normal +Z, board on inside (z = +INSET)
        return new THREE.Vector3(c.x, c.y, WALL_INSET);
      case 'back':
        // z=roomWidth plane, normal -Z, board on inside (z = roomWidth - INSET)
        // Mirror x so boards appear correct from inside
        return new THREE.Vector3(roomLength - c.x, c.y, roomWidth - WALL_INSET);
      case 'left':
        // x=0 plane, normal +X, board on inside (x = +INSET)
        return new THREE.Vector3(WALL_INSET, c.y, c.x);
      case 'right':
        // x=roomLength plane, normal -X, board on inside (x = roomLength - INSET)
        // Mirror x so boards appear correct from inside
        return new THREE.Vector3(roomLength - WALL_INSET, c.y, roomWidth - c.x);
    }
  }) as [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3];

  return corners3d;
}

/**
 * Create a merged mesh from multiple board quads.
 * Each board = 2 triangles = 6 vertices (using index buffer: 4 vertices + 6 indices).
 */
function createMergedBoardMesh(
  boards: BoardRect[],
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const numBoards = boards.length;
  const positions = new Float32Array(numBoards * 4 * 3); // 4 verts × 3 components
  const normals = new Float32Array(numBoards * 4 * 3);
  const indices: number[] = [];

  // Compute face normal
  const normal = getFaceNormal(face);

  for (let i = 0; i < numBoards; i++) {
    const [bl, br, tr, tl] = boardTo3D(boards[i], face, roomLength, roomWidth, roomHeight);
    const offset = i * 4 * 3;
    const vertOffset = i * 4;

    // Positions: BL, BR, TR, TL
    positions[offset] = bl.x;     positions[offset + 1] = bl.y;     positions[offset + 2] = bl.z;
    positions[offset + 3] = br.x; positions[offset + 4] = br.y;     positions[offset + 5] = br.z;
    positions[offset + 6] = tr.x; positions[offset + 7] = tr.y;     positions[offset + 8] = tr.z;
    positions[offset + 9] = tl.x; positions[offset + 10] = tl.y;    positions[offset + 11] = tl.z;

    // Normals (all same for a flat wall)
    for (let v = 0; v < 4; v++) {
      normals[offset + v * 3] = normal.x;
      normals[offset + v * 3 + 1] = normal.y;
      normals[offset + v * 3 + 2] = normal.z;
    }

    // Indices: two triangles (BL, BR, TR) and (BL, TR, TL)
    indices.push(
      vertOffset, vertOffset + 1, vertOffset + 2,
      vertOffset, vertOffset + 2, vertOffset + 3
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return new THREE.Mesh(geometry, material);
}

/**
 * Create merged board mesh for ceiling boards.
 * Boards are in XZ plane at Y=roomHeight.
 */
function createMergedCeilingBoardMesh(
  boards: BoardRect[],
  roomLength: number,
  _roomWidth: number,
  roomHeight: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const numBoards = boards.length;
  const positions = new Float32Array(numBoards * 4 * 3);
  const normals = new Float32Array(numBoards * 4 * 3);
  const indices: number[] = [];

  // Ceiling normal faces down (into room)
  const normal = new THREE.Vector3(0, -1, 0);

  for (let i = 0; i < numBoards; i++) {
    const board = boards[i];
    const offset = i * 4 * 3;
    const vertOffset = i * 4;

    // Map board 2D (x=along length, y=along width) to 3D (X, roomHeight+inset, Z)
    const yPos = roomHeight - WALL_INSET;
    const x1 = board.x;
    const x2 = board.x + board.width;
    const z1 = board.y;
    const z2 = board.y + board.height;

    // BL, BR, TR, TL in XZ plane
    positions[offset] = x1;     positions[offset + 1] = yPos; positions[offset + 2] = z1;
    positions[offset + 3] = x2; positions[offset + 4] = yPos; positions[offset + 5] = z1;
    positions[offset + 6] = x2; positions[offset + 7] = yPos; positions[offset + 8] = z2;
    positions[offset + 9] = x1; positions[offset + 10] = yPos; positions[offset + 11] = z2;

    for (let v = 0; v < 4; v++) {
      normals[offset + v * 3] = normal.x;
      normals[offset + v * 3 + 1] = normal.y;
      normals[offset + v * 3 + 2] = normal.z;
    }

    indices.push(
      vertOffset, vertOffset + 1, vertOffset + 2,
      vertOffset, vertOffset + 2, vertOffset + 3
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  return new THREE.Mesh(geometry, material);
}

/**
 * Create seam lines around every board edge for clear visibility.
 */
function createSeamLineOverlay(
  boards: BoardRect[],
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.LineSegments | null {
  if (boards.length === 0) return null;

  const points: number[] = [];

  for (const board of boards) {
    const [bl, br, tr, tl] = boardTo3D(board, face, roomLength, roomWidth, roomHeight);

    // All 4 edges of each board
    // Bottom
    points.push(bl.x, bl.y, bl.z, br.x, br.y, br.z);
    // Right
    points.push(br.x, br.y, br.z, tr.x, tr.y, tr.z);
    // Top
    points.push(tr.x, tr.y, tr.z, tl.x, tl.y, tl.z);
    // Left
    points.push(tl.x, tl.y, tl.z, bl.x, bl.y, bl.z);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  const material = new THREE.LineBasicMaterial({
    color: 0x666666,
    linewidth: 1,
    transparent: true,
    opacity: 0.5,
  });

  return new THREE.LineSegments(geometry, material);
}

/**
 * Create line segments for cut edges.
 */
function createCutLineOverlay(
  boards: BoardRect[],
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.LineSegments | null {
  const cutBoards = boards.filter(b => b.isCut);
  if (cutBoards.length === 0) return null;

  const points: number[] = [];

  for (const board of cutBoards) {
    const [bl, br, tr, tl] = boardTo3D(board, face, roomLength, roomWidth, roomHeight);

    // Draw a line on the cut edge
    switch (board.cutEdge) {
      case 'right':
        points.push(br.x, br.y, br.z, tr.x, tr.y, tr.z);
        break;
      case 'top':
        points.push(tl.x, tl.y, tl.z, tr.x, tr.y, tr.z);
        break;
      case 'left':
        points.push(bl.x, bl.y, bl.z, tl.x, tl.y, tl.z);
        break;
      case 'bottom':
        points.push(bl.x, bl.y, bl.z, br.x, br.y, br.z);
        break;
    }
  }

  if (points.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  const material = new THREE.LineBasicMaterial({
    color: 0xaa4444,
    linewidth: 1,
    transparent: true,
    opacity: 0.7,
  });

  return new THREE.LineSegments(geometry, material);
}

/**
 * Create seam lines for ceiling boards (XZ plane at Y=roomHeight).
 * Matches the wall seam line style.
 */
function createCeilingSeamLines(
  boards: BoardRect[],
  roomLength: number,
  roomHeight: number
): THREE.LineSegments | null {
  if (boards.length === 0) return null;

  const points: number[] = [];
  const yPos = roomHeight - WALL_INSET + 0.001;

  for (const board of boards) {
    const x1 = board.x;
    const x2 = board.x + board.width;
    const z1 = board.y;
    const z2 = board.y + board.height;

    // All 4 edges
    points.push(x1, yPos, z1, x2, yPos, z1); // front
    points.push(x2, yPos, z1, x2, yPos, z2); // right
    points.push(x2, yPos, z2, x1, yPos, z2); // back
    points.push(x1, yPos, z2, x1, yPos, z1); // left
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  const material = new THREE.LineBasicMaterial({
    color: 0x666666,
    linewidth: 1,
    transparent: true,
    opacity: 0.5,
  });

  return new THREE.LineSegments(geometry, material);
}

/**
 * Create groove lines for SlatWall sections.
 *
 * SlatWall panels are 6" (0.5ft) tall with interlocking edges. When stacked,
 * the result is evenly-spaced horizontal groove channels every ~2" (3 per panel).
 * This creates the distinctive horizontal line pattern seen in real installations.
 *
 * Each 6" panel contributes 3 groove lines:
 *   - Bottom interlock seam
 *   - Lower groove channel (~2" up)
 *   - Upper groove channel (~4" up)
 * The top interlock seam of one panel aligns with the bottom of the next.
 */
function createSlatWallGrooves(
  slatWallBoards: BoardRect[],
  face: WallFace,
  roomLength: number,
  roomWidth: number,
  roomHeight: number
): THREE.LineSegments | null {
  if (slatWallBoards.length === 0) return null;

  const points: number[] = [];
  // 2 grooves per 6" (0.5ft) panel, at 1/3 and 2/3 height within each panel
  const PANEL_HEIGHT = 0.5; // 6 inches in feet

  const SW_PANEL_LENGTH = 8; // SlatWall panels are 8' long
  // 2 grooves + 1 panel seam per 6" panel = line every 2" (0.167ft)
  const LINE_SPACING = 2 / 12; // 2 inches in feet

  for (const board of slatWallBoards) {
    // Horizontal lines every 2" across entire section — evenly spaced
    const numLines = Math.floor(board.height / LINE_SPACING);
    for (let g = 1; g <= numLines; g++) {
      const lineY = board.y + g * LINE_SPACING;
      if (lineY >= board.y + board.height - 0.001) continue;

      const leftPt = boardTo3D(
        { ...board, x: board.x, y: lineY, width: 0, height: 0 } as BoardRect,
        face, roomLength, roomWidth, roomHeight
      )[0];
      const rightPt = boardTo3D(
        { ...board, x: board.x + board.width, y: lineY, width: 0, height: 0 } as BoardRect,
        face, roomLength, roomWidth, roomHeight
      )[0];

      points.push(leftPt.x, leftPt.y, leftPt.z, rightPt.x, rightPt.y, rightPt.z);
    }

    // Vertical seam lines at 8' panel joins
    const numPanelCols = Math.ceil(board.width / SW_PANEL_LENGTH);
    for (let c = 1; c < numPanelCols; c++) {
      const seamX = board.x + c * SW_PANEL_LENGTH;
      if (seamX >= board.x + board.width - 0.001) continue;

      const bottomPt = boardTo3D(
        { ...board, x: seamX, y: board.y, width: 0, height: 0 } as BoardRect,
        face, roomLength, roomWidth, roomHeight
      )[0];
      const topPt = boardTo3D(
        { ...board, x: seamX, y: board.y + board.height, width: 0, height: 0 } as BoardRect,
        face, roomLength, roomWidth, roomHeight
      )[0];

      points.push(bottomPt.x, bottomPt.y, bottomPt.z, topPt.x, topPt.y, topPt.z);
    }
  }

  if (points.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));

  // Subtle groove lines — lighter than W&CB seams for cleaner appearance
  const material = new THREE.LineBasicMaterial({
    color: 0xaaaaaa,
    linewidth: 1,
    transparent: true,
    opacity: 0.35,
  });

  return new THREE.LineSegments(geometry, material);
}

/**
 * Get the inward-facing normal for a wall face.
 */
function getFaceNormal(face: WallFace): THREE.Vector3 {
  switch (face) {
    case 'front': return new THREE.Vector3(0, 0, 1);    // Into room
    case 'back':  return new THREE.Vector3(0, 0, -1);
    case 'left':  return new THREE.Vector3(1, 0, 0);
    case 'right': return new THREE.Vector3(-1, 0, 0);
  }
}
